import React, { useState, useEffect, useRef } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import styles from './AdminUserDetail.module.css';
import { userApi } from '../../services/userApi';

interface UserDetailParams {
  userId: string;
}

interface Firm {
  Firm_ID: string;
  abbrev: string;
  name?: string;
  logo?: string;
}

interface City {
  City_ID: string;
  name: string;
  Country?: {
    name: string;
  };
}

interface User {
  User_ID: string;
  firstname: string;
  lastname: string;
  title?: string;
  mail: string;
  jobtitle?: string;
  picture?: string;
  created_at: string;
  updated_at: string;
  last_login?: string;
  linkedin?: string;
  profile?: string;
  gender?: string;
  is_scim?: boolean;
  department?: string;
  employee_group?: string;
  firm?: Firm;
  City_ID?: string;
  Firm_ID?: string;
  verification?: number;
}

const Admin_User_Detail: React.FC = () => {
  const { userId } = useParams<{ userId: string }>();
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [editMode, setEditMode] = useState<boolean>(true); // Start in edit mode since we're matching the template
  const [formData, setFormData] = useState<Partial<User>>({});
  const [firms, setFirms] = useState<Firm[]>([]);
  const [cities, setCities] = useState<City[]>([]);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const navigate = useNavigate();
  const isMountedRef = useRef<boolean>(true);

  // Helper function to extract error message from API response
  const getErrorMessage = async (error: any): Promise<string> => {
    if (error?.response?.json) {
      try {
        const errorData = await error.response.json();
        return errorData.detail || errorData.message || 'An error occurred';
      } catch {
        return error.message || 'An error occurred';
      }
    } else if (error?.detail) {
      return error.detail;
    } else if (error?.message) {
      return error.message;
    } else if (typeof error === 'string') {
      return error;
    }
    return 'An unexpected error occurred';
  };

  // Avoid navigating too quickly which can cause ResizeObserver issues
  const navigateWithDelay = (path: string) => {
    // Short delay to allow cleanup
    setTimeout(() => {
      if (isMountedRef.current) {
        navigate(path);
      }
    }, 50);
  };

  useEffect(() => {
    if (userId) {
      fetchUser();
      fetchFirms();
      fetchCities();
    }
    
    // Cleanup function to handle unmounting gracefully
    return () => {
      isMountedRef.current = false;
    };
  }, [userId]);

  useEffect(() => {
    // Set image preview when user data is loaded
    if (user?.picture) {
      setImagePreview(`data:image/png;base64,${user.picture}`);
    }
  }, [user]);

  const fetchUser = async () => {
    try {
      setLoading(true);
      const userData = await userApi.getUserById(userId!);
      setUser(userData);
      setFormData(userData);
            
      setLoading(false);
    } catch (error) {
      console.error('Error fetching user:', error);
      const errorMessage = await getErrorMessage(error);
      setError(`Failed to load user data: ${errorMessage}`);
      setLoading(false);
      toast.error(`Failed to load user data: ${errorMessage}`, {
        position: "top-right",
        autoClose: 5000
      });
    }
  };

  const fetchFirms = async () => {
    try {
      const firmsData = await userApi.getFirmsLookup();
      setFirms(firmsData);
    } catch (error) {
      console.error('Error fetching firms:', error);
      const errorMessage = await getErrorMessage(error);
      toast.error(`Failed to load firms data: ${errorMessage}`, {
        position: "top-right",
        autoClose: 5000
      });
    }
  };

  const fetchCities = async () => {
    try {
      const citiesData = await userApi.getCitiesLookup();
      setCities(citiesData);
    } catch (error) {
      console.error('Error fetching cities:', error);
      const errorMessage = await getErrorMessage(error);
      toast.error(`Failed to load cities data: ${errorMessage}`, {
        position: "top-right",
        autoClose: 5000
      });
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { id, value, type } = e.target as HTMLInputElement;
    
    if (type === 'checkbox') {
      const isChecked = (e.target as HTMLInputElement).checked;
      if (id === 'inputVerification') {
        setFormData(prev => ({
          ...prev,
          verification: isChecked ? 1 : 0
        }));
      } else {
        setFormData(prev => ({
          ...prev,
          [id.replace('input', '').toLowerCase()]: isChecked
        }));
      }
    } else if (id === 'inputfirm') {
      setFormData(prev => ({
        ...prev,
        Firm_ID: value
      }));
    } else if (id === 'inputcity') {
      setFormData(prev => ({
        ...prev,
        City_ID: value
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [id.replace('input', '').toLowerCase()]: value
      }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    // Prevent any form of page reload
    e.preventDefault();
    e.stopPropagation();
    
    if (!userId) return;
    
    try {
      // Show a loading indicator in the button or elsewhere if needed
      const updatedUser = await userApi.updateUser(userId, formData);
      
      // Update local state with the updated user data
      setUser(updatedUser);
      
      // Show success toast
      toast.success('User updated successfully', {
        position: "top-right",
        autoClose: 4000
      });
      
      // No need to call fetchUser() as we already have the updated data
      // Just update any other pieces of state if needed
    } catch (error) {
      console.error('Error updating user:', error);
      const errorMessage = await getErrorMessage(error);
      toast.error(`Failed to update user: ${errorMessage}`, {
        position: "top-right",
        autoClose: 5000
      });
    }
  };

  const handleDelete = async () => {
    if (!userId) {
      return;
    }
    
    // Instead of window.confirm, use toast.warn with a custom UI for confirmation
    toast.warn(
      <div style={{ minWidth: '300px' }}>
        <p className="mb-3 fw-bold">Are you sure you want to delete this user?</p>
        <div className="d-flex justify-content-center gap-3">
          <button 
            className="btn btn-secondary px-4" 
            onClick={() => toast.dismiss()}
          >
            Cancel
          </button>
          <button 
            className="btn btn-danger px-4" 
            onClick={async () => {
              toast.dismiss();
              try {
                await userApi.deleteUser(userId);
                
                // Force navigation using window.location as backup
                if (navigate) {
                  navigate('/admin/users', { replace: true });
                } else {
                  window.location.href = '/admin/users';
                }
                
                // Show success toast after navigation
                setTimeout(() => {
                  toast.success('User deleted successfully', {
                    position: "top-right",
                    autoClose: 3000,
                    hideProgressBar: false,
                    closeOnClick: true,
                    pauseOnHover: true,
                    draggable: true
                  });
                }, 100);
              } catch (error) {
                console.error('Error deleting user:', error);
                const errorMessage = await getErrorMessage(error);
                toast.error(`Failed to delete user: ${errorMessage}`, {
                  position: "top-right",
                  autoClose: 3000,
                  hideProgressBar: false,
                  closeOnClick: true,
                  pauseOnHover: true,
                  draggable: true
                });
              }
            }}
          >
            Delete
          </button>
        </div>
      </div>,
      {
        position: "top-center",
        autoClose: false,
        hideProgressBar: false,
        closeOnClick: false,
        pauseOnHover: true,
        draggable: true,
        closeButton: false,
        className: styles.deleteConfirmationToast,
        style: { 
          maxWidth: '400px', 
          width: '100%',
          padding: '10px'
        }
      }
    );
  };

  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        // Extract base64 data
        const fullBase64 = reader.result?.toString();
        const base64String = fullBase64?.split(',')[1];
        if (base64String) {
          setFormData(prev => ({
            ...prev,
            picture: base64String
          }));
          // Set image preview with full base64 string for immediate display
          setImagePreview(fullBase64 || null);
        }
      };
      reader.readAsDataURL(file);
    }
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return `${date.getDate().toString().padStart(2, '0')}.${(date.getMonth() + 1)
      .toString().padStart(2, '0')}.${date.getFullYear()} ${date.getHours().toString()
      .padStart(2, '0')}:${date.getMinutes().toString().padStart(2, '0')}`;
  };

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  if (loading) {
    return <div className={`container mt-5 ${styles.loadingContainer}`}><div className="spinner-border" role="status"></div></div>;
  }

  if (error || !user) {
    return <div className={`container mt-5 alert alert-danger ${styles.errorAlert}`}>{error || 'User not found'}</div>;
  }

  return (
    <div className={styles.root}>
       <div className="container mt-4">
        <nav aria-label="breadcrumb">
          <ol className={`breadcrumb `}>
            <li className={`breadcrumb-item `}>
              <Link to="/admin"><i className="bi bi-house-door me-1"></i>Admin</Link>
            </li>
            <li className={`breadcrumb-item `}>
              <Link to="/admin/users">User Management</Link>
            </li>
            <li className={`breadcrumb-item active `} aria-current="page">
              {user.title} {user.firstname} {user.lastname}
            </li>
          </ol>
        </nav>

        {/* Roles Management Button */}
        <div className="row mb-3">
          <div className="col d-flex justify-content-end">
            <Link 
              className={`btn btn-sm btn-primary ${styles.rolesButton}`} 
              to={`/admin/users/${user.User_ID}/roles`} 
            >
              Roles Management
            </Link>
          </div>
        </div>

        {/* SCIM Warning */}
        {user.is_scim && (
          <div className={`row ${styles.scimWarning}`}>
            <div className="alert alert-warning" role="alert">
              This user is managed via SCIM. Some fields cannot be edited and must be changed by the system administrator.
            </div>
          </div>
        )}

        <div className="container-xl px-4 mt-4">
          <form id="profileForm" onSubmit={(e) => e.preventDefault()} noValidate>
            <div className="row">
              <div className="col-xl-4">
                {/* Profile picture card */}
                <div className={`card mb-4 mb-xl-0 ${styles.card}`}>
                  <div className={`card-header ${styles.cardHeader}`}>Profile Picture</div>
                  <div className={`card-body text-center ${styles.cardBody}`}>
                    {/* Profile picture image */}
                    {imagePreview ? (
                      <img 
                        id="profileImage" 
                        className={`img-account-profile rounded-circle mb-2 ${styles.profilePicture}`}
                        src={imagePreview} 
                        alt="Profile Picture" 
                      />
                    ) : user?.picture ? (
                      <img 
                        id="profileImage" 
                        className={`img-account-profile rounded-circle mb-2 ${styles.profilePicture}`}
                        src={`data:image/png;base64,${user.picture}`} 
                        alt="Profile Picture" 
                      />
                    ) : (
                      <img 
                        id="profileImage" 
                        className={`img-account-profile rounded-circle mb-2 ${styles.profilePicture}`}
                        src="/static/images/user-profile-icon.png" 
                        alt="Default Profile Picture" 
                      />
                    )}

                    {/* Profile picture help block */}
                    <div className={`small font-italic text-muted mb-4 ${styles.profileUploadInfo}`}>JPG or PNG no larger than 5 MB</div>

                    {/* Hidden file input */}
                    <input 
                      type="file" 
                      id="fileInput" 
                      accept="image/png, image/jpeg" 
                      className="d-none"
                      onChange={handleFileUpload} 
                    />

                    {/* Profile picture upload button */}
                    <button 
                      id="uploadButton" 
                      className="btn btn-primary" 
                      type="button"
                      onClick={() => document.getElementById('fileInput')?.click()}
                    >
                      Upload new image
                    </button>
                  </div>
                </div>
              </div>

              <div className="col-xl-8">
                {/* Account details card */}
                <div className={`card mb-4 ${styles.card}`}>
                  <div className={`card-header ${styles.cardHeader}`}>Account Details</div>
                  <div className={`card-body ${styles.cardBody}`}>
                    {/* First name and Last name */}
                    <div className={`row gx-3 mb-3 ${styles.formRow}`}>
                      {/* Form Group (first name) */}
                      <div className={`col-md-6 ${styles.formGroup}`}>
                        <label className={`small mb-1 ${styles.formLabel}`} htmlFor="inputFirstname">First name</label>
                        <input 
                          className={`form-control ${styles.formControl}`} 
                          id="inputFirstname" 
                          type="text"
                          placeholder="Enter first name" 
                          value={formData.firstname || ''} 
                          onChange={handleInputChange}
                          required
                          disabled={user.is_scim}
                        />
                      </div>
                      {/* Form Group (last name) */}
                      <div className={`col-md-6 ${styles.formGroup}`}>
                        <label className={`small mb-1 ${styles.formLabel}`} htmlFor="inputLastname">Last name</label>
                        <input 
                          className={`form-control ${styles.formControl}`} 
                          id="inputLastname" 
                          type="text"
                          placeholder="Enter last name" 
                          value={formData.lastname || ''} 
                          onChange={handleInputChange}
                          required
                          disabled={user.is_scim}
                        />
                      </div>
                    </div>
                    
                    {/* Title and Verification */}
                    <div className={`row gx-3 mb-3 ${styles.formRow}`}>
                      {/* Form Group (title) */}
                      <div className={`col-md-6 ${styles.formGroup}`}>
                        <label className={`small mb-1 ${styles.formLabel}`} htmlFor="inputTitle">Title</label>
                        <input 
                          className={`form-control ${styles.formControl}`} 
                          id="inputTitle" 
                          type="text"
                          placeholder="Enter title (e.g. Dr., Prof.)" 
                          value={formData.title || ''} 
                          onChange={handleInputChange}
                          disabled={user.is_scim}
                        />
                      </div>

                      <div className={`col-md-6 ${styles.formGroup}`}>
                        <div className="form-check mt-4">
                          <input 
                            className="form-check-input" 
                            type="checkbox" 
                            id="inputVerification" 
                            checked={formData.verification === 1}
                            onChange={handleInputChange}
                          />
                          <label className={`form-check-label ${styles.formLabel}`} htmlFor="inputVerification">Verification</label>
                        </div>
                      </div>
                    </div>

                    {/* Email address */}
                    <div className={`row gx-3 mb-3 ${styles.formRow}`}>
                      {/* Form Group (email address) */}
                      <div className={`col-md-12 ${styles.formGroup}`}>
                        <label className={`small mb-1 ${styles.formLabel}`} htmlFor="inputMail">Email address</label>
                        <input 
                          className={`form-control ${styles.formControl}`} 
                          id="inputMail" 
                          type="email"
                          placeholder="Enter email address" 
                          value={formData.mail || ''} 
                          onChange={handleInputChange}
                          required
                          disabled={user.is_scim}
                        />
                      </div>
                    </div>

                    {/* Firm */}
                    <div className={`row gx-3 mb-3 ${styles.formRow}`}>
                      {/* Form Group (firm) */}
                      <div className={`col-md-12 ${styles.formGroup}`}>
                        <label className={`small mb-1 ${styles.formLabel}`} htmlFor="inputfirm">Firm</label>
                        <select 
                          className={`form-control ${styles.formControl}`} 
                          id="inputfirm"
                          value={formData.Firm_ID || ''} 
                          onChange={handleInputChange}
                          disabled={user.is_scim}
                        >
                          <option value="">Select a firm</option>
                          {firms.map(firm => (
                            <option key={firm.Firm_ID} value={firm.Firm_ID}>
                              {firm.name || firm.abbrev}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>

                    {/* LinkedIn and Profile */}
                    <div className={`row gx-3 mb-3 ${styles.formRow}`}>
                      <div className={`col-md-6 ${styles.formGroup}`}>
                        <label className={`small mb-1 ${styles.formLabel}`} htmlFor="inputLinkedin">LinkedIn</label>
                        <input 
                          className={`form-control ${styles.formControl}`} 
                          id="inputLinkedin" 
                          type="url"
                          placeholder="Enter LinkedIn URL" 
                          value={formData.linkedin || ''} 
                          onChange={handleInputChange}
                        />
                      </div>
                      <div className={`col-md-6 ${styles.formGroup}`}>
                        <label className={`small mb-1 ${styles.formLabel}`} htmlFor="inputProfile">Profile</label>
                        <input 
                          className={`form-control ${styles.formControl}`} 
                          id="inputProfile" 
                          type="text"
                          placeholder="Enter profile" 
                          value={formData.profile || ''} 
                          onChange={handleInputChange}
                        />
                      </div>
                    </div>

                    {/* Job Title and Gender */}
                    <div className={`row gx-3 mb-3 ${styles.formRow}`}>
                      <div className={`col-md-6 ${styles.formGroup}`}>
                        <label className={`small mb-1 ${styles.formLabel}`} htmlFor="inputJobtitle">Jobtitle</label>
                        <input 
                          className={`form-control ${styles.formControl}`} 
                          id="inputJobtitle" 
                          type="text"
                          placeholder="Enter job title" 
                          value={formData.jobtitle || ''} 
                          onChange={handleInputChange}
                          required
                          disabled={user.is_scim}
                        />
                      </div>
                      <div className={`col-md-6 ${styles.formGroup}`}>
                        <label className={`small mb-1 ${styles.formLabel}`} htmlFor="inputGender">Gender</label>
                        <select 
                          className={`form-control ${styles.formControl}`} 
                          id="inputGender"
                          value={formData.gender || ''} 
                          onChange={handleInputChange}
                          required
                          disabled={user.is_scim}
                        >
                          <option value="">Select a gender</option>
                          <option value="Male">Male</option>
                          <option value="Female">Female</option>
                          <option value="Diverse">Diverse</option>
                        </select>
                      </div>
                    </div>

                    {/* City */}
                    <div className={`row gx-3 mb-3 ${styles.formRow}`}>
                      <div className={`col-md-12 ${styles.formGroup}`}>
                        <label className={`small mb-1 ${styles.formLabel}`} htmlFor="inputcity">City</label>
                        <select 
                          className={`form-control ${styles.formControl}`} 
                          id="inputcity"
                          value={formData.City_ID || ''} 
                          onChange={handleInputChange}
                          disabled={user.is_scim}
                        >
                          <option value="">Select a city</option>
                          {cities.map(city => (
                            <option key={city.City_ID} value={city.City_ID}>
                              {city.name} {city.Country && `(${city.Country.name})`}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>


                    <hr />

                    <div className={`row gx-3 mb-3 ${styles.formRow}`}>
                      {/* Form Group (department) */}
                      <div className={`col-md-6 ${styles.formGroup}`}>
                        <label className={`small mb-1 ${styles.formLabel}`} htmlFor="inputDepartment">Department</label>
                        <input 
                          className={`form-control ${styles.formControl}`} 
                          id="inputDepartment" 
                          type="text"
                          placeholder="Enter department" 
                          value={formData.department || ''} 
                          onChange={handleInputChange}
                          disabled={user.is_scim}
                        />
                      </div>
                      {/* Form Group (employee group) */}
                      <div className={`col-md-6 ${styles.formGroup}`}>
                        <label className={`small mb-1 ${styles.formLabel}`} htmlFor="inputEmployee_group">Employee Group</label>
                        <input 
                          className={`form-control ${styles.formControl}`} 
                          id="inputEmployee_group" 
                          type="text"
                          placeholder="Enter employee group" 
                          value={formData.employee_group || ''} 
                          onChange={handleInputChange}
                          disabled={user.is_scim}
                        />
                      </div>
                    </div>

                    <hr />

                    <div className={`row gx-3 mb-3 ${styles.formRow}`}>
                      {/* Creation, Update and Last Login Dates */}
                      <div className={`col-md-4 ${styles.formGroup}`}>
                        <label className={`small mb-1 ${styles.formLabel}`}>Created At</label>
                        <p className={styles.formControlStatic}>{formatDate(user.created_at)}</p>
                      </div>
                      <div className={`col-md-4 ${styles.formGroup}`}>
                        <label className={`small mb-1 ${styles.formLabel}`}>Updated At</label>
                        <p className={styles.formControlStatic}>{formatDate(user.updated_at)}</p>
                      </div>
                      <div className={`col-md-4 ${styles.formGroup}`}>
                        <label className={`small mb-1 ${styles.formLabel}`}>Last Login At</label>
                        <p className={styles.formControlStatic}>{user.last_login ? formatDate(user.last_login) : 'Never'}</p>
                      </div>
                    </div>

                    {/* Save and Delete buttons */}
                    <div className={styles.formButtons}>
                      <button 
                        className={`btn btn-primary me-md-2 ${styles.btnPrimary}`} 
                        type="button" 
                        onClick={handleSubmit}
                      >
                        Save changes
                      </button>
                      <button 
                        className={`btn btn-danger ${styles.btnDanger}`} 
                        type="button"
                        onClick={handleDelete}
                      >
                        Delete User
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </form>
        </div>
      </div>
      <ToastContainer 
        position="top-right"
        autoClose={3000}
        hideProgressBar={false}
        newestOnTop={true}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss={false}
        draggable
        pauseOnHover
        style={{ zIndex: 9999 }}
        limit={3}
      />
    </div>
  );
};

export default Admin_User_Detail;
